/* eslint-disable max-len */
import React, { useContext, useEffect, useRef, useState } from 'react';
import detectBrowserLanguage from 'detect-browser-language';
import useSWR from 'swr';
import {
  Box,
  Stack,
  Typography,
  Card,
  Chip,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Button,
  Fade,
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ViewCarouselRoundedIcon from '@mui/icons-material/ViewCarouselRounded';
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import {
  Settings as SettingsIcon, TrendingUpDown, Eye, Palette, LayoutGrid, MonitorPlay,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Page } from 'Components';
import { AppContext } from 'Context';
import { axios as apiAxios, dataFetcher, endpoints, getToken } from 'Api';
import { transitionType } from 'Commons';
import CategoryCreateButton from 'Pages/private/content-layout/CategoryCreateButton';
import ContentGroupButton from 'Pages/private/content-layout/ContentGroupButton';
import CreateMenu from 'Pages/private/content-layout/CreateMenu';
import PillNav from 'Components/reactbits/PillNav';
import ElasticSlider from 'Components/reactbits/ElasticSlider';
import { useViewerMode } from 'hooks/useViewerMode';
import {
  useProfileBg, PROFILE_BG_PRESETS, PROFILE_BG_DEFAULT, DEFAULT_BG_COLOR,
  bgSwatch, bgSolid,
} from 'hooks/useProfileBg';
import {
  getThumbnailTileStyle, setThumbnailTileStyle,
  getPlayerUiStyle, setPlayerUiStyle, PLAYER_UI_STYLE_OPTIONS,
} from 'Config/featureFlags';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ViewerAurora } from 'Components/viewer-inner';
import { AnimatedCategory, AnimatedPlaylist, AnimatedAlbum } from 'Components/reactbits/AnimatedIcons';
import DisplayNameSettings from 'Pages/private/content-layout/DisplayNameSettings';
import { subscribeCondense, setCondense } from 'Utils/scrollCondense';
import { V2ThemeProvider } from '../_shared/V2Layout';
import { SpotlightCard } from '../_shared/SpotlightCard';
import ProjectorPreview from './ProjectorPreview';
import ProjectorNameEdit from './ProjectorNameEdit';
import styles from './index.module.css';

const MotionCard = motion(Card);

// Brand accent used for the section icon chips (matches the app's #5aa5ef blue).
const ACCENT = '#5aa5ef';

const SectionCard = ({ icon: Icon, title, description, action, children, sx, delay = 0 }) => (
  <MotionCard
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -2 }}
    sx={{
      p: { xs: 2.5, md: 3 },
      mb: 2.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 10px 28px rgba(16,24,40,0.05)',
      transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
      '&:hover': {
        boxShadow: '0 4px 10px rgba(16,24,40,0.06), 0 18px 44px rgba(16,24,40,0.09)',
        borderColor: 'rgba(90,165,239,0.4)',
      },
      ...sx,
    }}
  >
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'rgba(90,165,239,0.12)',
            color: ACCENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
          {description && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
      {action}
    </Stack>
    <Box>{children}</Box>
  </MotionCard>
);

// Masonry tile for the redesigned Content Layout: a dashboard-style SpotlightCard
// (cursor-follow glow) with an icon-chip header, animated in. `break-inside: avoid`
// lets it pack into the CSS multi-column masonry without splitting across columns.
const MasonryCard = ({
  icon: Icon, title, description, action, children, delay = 0, spotlightColor, accent = ACCENT,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ breakInside: 'avoid', marginBottom: 20 }}
  >
    <SpotlightCard
      spotlightColor={spotlightColor}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 3.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 14px 36px rgba(16,24,40,0.06)',
        transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
        '&:hover': { transform: 'translateY(-2px)', borderColor: 'rgba(90,165,239,0.35)' },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, position: 'relative', zIndex: 1,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              bgcolor: `${accent}22`,
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16.5, letterSpacing: '-0.01em' }}>{title}</Typography>
            {description && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{description}</Typography>
            )}
          </Box>
        </Stack>
        {action}
      </Stack>
      <Box sx={{ position: 'relative', zIndex: 1 }}>{children}</Box>
    </SpotlightCard>
  </motion.div>
);

// Map a transition's name to a distinct framer in-animation for the preview.
const transitionVariants = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('spin')) return { initial: { rotate: -180, scale: 0.5, opacity: 0 }, animate: { rotate: 0, scale: 1, opacity: 1 } };
  if (n.includes('flip') || n.includes('cube')) return { initial: { rotateY: 90, opacity: 0 }, animate: { rotateY: 0, opacity: 1 } };
  if (n.includes('zoom') || n.includes('scale')) return { initial: { scale: 0.3, opacity: 0 }, animate: { scale: 1, opacity: 1 } };
  if (n.includes('fade') || n.includes('dissolve')) return { initial: { opacity: 0 }, animate: { opacity: 1 } };
  if (n.includes('up')) return { initial: { y: '100%', opacity: 0.4 }, animate: { y: '0%', opacity: 1 } };
  if (n.includes('down')) return { initial: { y: '-100%', opacity: 0.4 }, animate: { y: '0%', opacity: 1 } };
  if (n.includes('left')) return { initial: { x: '-100%', opacity: 0.4 }, animate: { x: '0%', opacity: 1 } };
  // "Slide In" / slide / right and default
  return { initial: { x: '100%', opacity: 0.4 }, animate: { x: '0%', opacity: 1 } };
};

// Two-rectangle demo (gray fill, black stroke): a back "photo" and a front one that
// animates in with the selected transition, on a loop, so the UX is clear.
const TransitionPreview = ({ typeName }) => {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setCycle((c) => c + 1), 1700);
    return () => clearInterval(id);
  }, []);
  const v = transitionVariants(typeName);
  const rect = {
    position: 'absolute',
    inset: 0,
    borderRadius: 8,
    boxSizing: 'border-box',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
  };
  return (
    <Box sx={{
      position: 'relative', width: 116, height: 74, perspective: 600, overflow: 'hidden', borderRadius: 1.5, flexShrink: 0,
    }}
    >
      {/* Back slide (light) + incoming black slide that animates in with the
          selected transition — clean filled rectangles, no wireframe outline. */}
      <span style={{ ...rect, background: '#c9ced6' }} />
      <motion.span
        key={cycle}
        initial={v.initial}
        animate={v.animate}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ ...rect, background: '#15171c' }}
      />
    </Box>
  );
};

// Coming-soon placeholder for the Trending / Preview Style tabs.
const ComingSoon = ({ title, desc, Icon }) => (
  <Box sx={{
    py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.2,
  }}
  >
    <Box sx={{
      width: 54, height: 54, borderRadius: '50%', bgcolor: '#f2f4f7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#98a2b3',
    }}
    >
      {Icon ? <Icon size={24} strokeWidth={2} /> : null}
    </Box>
    <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{title}</Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 320 }}>{desc}</Typography>
    <Box sx={{
      mt: 1, px: 1.5, py: 0.5, borderRadius: 999, bgcolor: '#15171c', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
    }}
    >
      COMING SOON
    </Box>
  </Box>
);

const ContentLayoutInner = () => {
  // eslint-disable-next-line no-script-url
  window.location.href = 'javascript: $zopim.livechat.button.hide()';
  const {
    setAppSnackbar, langText, setTopOrBottom,
    // Bumped by the globally-mounted Create modals (CreateMenu opens them via
    // AppContext setters) on a successful create — this page needs to react
    // and refetch, since it doesn't own those modals itself.
    categoryRefreshTrigger, playlistRefreshTrigger,
    // Id/title of the playlist AddPlaylistModal just created — the refetch
    // this triggers can land before the server's read path is consistent with
    // its own write, so the new row is merged in from these rather than relied
    // on solely coming back from getMyPlaylist.
    newlyCreatedPlaylistId, newlyCreatedPlaylistTitle,
    // My own projector settings (top-level `settings` from getAllViewRequestSent).
    setMyProjectorSettings,
  } = useContext(AppContext);
  const layoutText = langText?.layout;
  const commonText = langText?.common;
  const browserLang = detectBrowserLanguage();

  useEffect(() => {
    setTopOrBottom(true);
    return () => setTopOrBottom(false);
  }, []);

  // Compact the title bar when the content is scrolled (scrollCondense bus).
  const [condensed, setCondensed] = useState(false);
  useEffect(() => subscribeCondense(setCondensed), []);

  // Projector display name (lifted out of ProjectorPreview so it can slide up
  // into the condensed header row next to Create / Preferences).
  const [displayName, setDisplayName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);
  useEffect(() => {
    let alive = true;
    apiAxios.get(endpoints.displayName, { params: { token: getToken() } })
      .then((res) => {
        const b = res?.data;
        if (alive && b?.success) setDisplayName(b?.display_name || b?.data?.display_name || '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const projectorLabel = displayName ? `${displayName}'s Projector` : 'Your Projector';
  const saveProjectorName = async () => {
    const trimmed = (draftName || '').trim();
    if (!trimmed || trimmed === displayName) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const res = await dataFetcher(endpoints.displayName, { display_name: trimmed });
      if (res?.success) {
        setDisplayName(trimmed);
        setAppSnackbar({ isVisible: true, message: res?.message || 'Display name updated' });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: res?.message || 'Could not update display name' });
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not update display name' });
    } finally {
      setSavingName(false);
      setEditingName(false);
    }
  };
  const nameEditorProps = {
    label: projectorLabel,
    editing: editingName,
    draft: draftName,
    saving: savingName,
    onStartEdit: () => { setDraftName(displayName || ''); setEditingName(true); },
    onDraftChange: setDraftName,
    onSave: saveProjectorName,
    onCancel: () => { setEditingName(false); setDraftName(''); },
  };
  const [categoriesData, setCategoriesData] = useState([]);
  const [playlistsData, setPlaylistsData] = useState([]);
  const [albumsData, setAlbumsData] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [transitionSpeed, setTransitionSpeed] = useState(7);
  const [settingsType, setSettingsType] = useState(undefined);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [selectedTransitionType, selectTransitionType] = useState(transitionType[0].class);
  const [selectedBannerType, selectBannerType] = useState(undefined);
  const [savedSettings, setSavedSettings] = useState({ type: null, speed: null });
  const [bannerTypes, setBannerTypes] = useState(undefined);
  const [categoryBoxes, setCategoryBoxes] = useState(undefined);
  const [selectedCategoryBox, selectCategoryBox] = useState(undefined);
  const [selectedContent, selectContent] = useState([]);
  const [savedBannerType, setSavedBannerType] = useState(undefined);
  const [savedContent, setSavedContent] = useState([]);
  const [updateType, setUpdateType] = useState(undefined);
  const [previewType, setPreviewType] = useState(undefined);
  const [selectedPreviewSettings, selectPreviewSettings] = useState(undefined);

  const EditButton = ({ onClick }) => (
    <button
      type="button"
      className={styles.v2EditButton}
      onClick={onClick}
    >
      <span>{commonText?.edit || 'Edit'}</span>
      <span className={styles.v2EditButtonChevron}>▾</span>
    </button>
  );

  // Full category count from the server (itemcount), independent of how many
  // rows have actually been paged in — the "Categories" badge shows this from
  // the very first response, before the rest have loaded.
  const [categoriesTotal, setCategoriesTotal] = useState(0);
  const loadingMoreCategoriesRef = useRef(false);
  const sortByOrderNumber = (list) => [...list].sort(({ order_number: a }, { order_number: b }) => {
    if (a > b) return 1;
    if (b > a) return -1;
    return 0;
  });

  const { mutate: getCategories, isValidating: gettingCategories } = useSWR(
    endpoints.getMyCategory,
    // First page only + no thumbnails: the layout preview only needs a light
    // sample of categories up front, not all of them with heavy thumbnail
    // payloads. The remaining pages are paged in on scroll via loadMoreCategories.
    (url) => dataFetcher(url, { limit: 30, start: 0, thumbnails: false }),
    {
      onSuccess: ({ success, data, itemcount }) => {
        if (success) {
          setCategoriesData(sortByOrderNumber(data || []));
          setCategoriesTotal(itemcount ?? data?.length ?? 0);
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching your categories' });
        }
      },
      onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching your categories' }),
    },
  );

  // Infinite-scroll page fetch for categories — appends the next `limit` rows
  // starting from how many are already loaded, until categoriesTotal is reached.
  // Guarded with a ref (not state) so rapid scroll events can't fire overlapping
  // requests before a re-render lands.
  const loadMoreCategories = async () => {
    if (loadingMoreCategoriesRef.current) return;
    if (categoriesData.length >= categoriesTotal) return;
    loadingMoreCategoriesRef.current = true;
    try {
      const res = await dataFetcher(endpoints.getMyCategory, {
        limit: 30, start: categoriesData.length, thumbnails: false,
      });
      if (res?.success) {
        setCategoriesData((prev) => sortByOrderNumber([...prev, ...(res.data || [])]));
        if (res.itemcount != null) setCategoriesTotal(res.itemcount);
      }
    } catch (e) {
      // Silent — the user can keep scrolling to retry; not worth an error toast.
    } finally {
      loadingMoreCategoriesRef.current = false;
    }
  };

  // Playlist the create modal just told us about, pending confirmation from the
  // next getMyPlaylist response — cleared as soon as that response lands so a
  // later deletion can never get re-added by this (a persistent context value
  // would keep re-inserting a "ghost" row forever).
  const pendingNewPlaylistRef = useRef(null);

  const { mutate: getPlaylists, isValidating: gettingPlaylists } = useSWR(
    endpoints.getMyPlaylist,
    // Drop thumbnail payloads — the layout preview only needs the list, not the
    // heavy media. (mutate() in the mount effect drives the single fetch, so the
    // old revalidateOnMount:true was just a redundant double-trigger.)
    (url) => dataFetcher(url, { thumbnails: false }),
    {
      onSuccess: ({ success, data }) => {
        if (success) {
          let list = data || [];
          const pending = pendingNewPlaylistRef.current;
          // Server can briefly lag behind its own write — keep the row the
          // create modal already told us about so the count doesn't dip back
          // down to what it was before the create until the server catches up.
          if (pending && !list.some((p) => String(p.id) === String(pending.id))) {
            list = [...list, { id: pending.id, title: pending.title || 'Untitled', video_count: 0 }];
          }
          pendingNewPlaylistRef.current = null;
          setPlaylistsData(list);
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching your categories' });
        }
      },
      onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching your categories' }),
    },
  );

  const { mutate: getAlbums, isValidating: gettingAlbums } = useSWR(
    endpoints.getMyAlbum,
    (url) => dataFetcher(url, { thumbnails: false }),
    {
      onSuccess: ({ success, data }) => {
        if (success) setAlbumsData(data || []);
      },
      // Albums are only used for the row count — fail quietly, don't nag the user.
      onError: () => {},
    },
  );

  const { mutate: getLayoutTypes, isValidating: gettingLayoutTypes } = useSWR(endpoints.getLayoutTypes, {
    onSuccess: ({ success, data, categoryStyles, bannerPreviewStyles }) => {
      if (success) {
        setBannerTypes(data);
        setCategoryBoxes(categoryStyles);
        setPreviewType(bannerPreviewStyles);
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching baner types' });
      }
    },
    onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching banner types' }),
  });

  const { mutate: getUserLayouts, isValidating: gettingUserLayouts } = useSWR(endpoints.getUserLayouts, {
    onSuccess: ({ success, layout_type_id, layouts, category_style_id, banner_preview_style_id }) => {
      if (success) {
        setSavedBannerType(layout_type_id);
        selectCategoryBox(category_style_id);
        if (layout_type_id && (layout_type_id === '3' || layout_type_id === '4' || layout_type_id === '5')) {
          setSavedContent(layouts);
        }
        selectBannerType(layout_type_id);
        if (banner_preview_style_id) {
          selectPreviewSettings(parseInt(banner_preview_style_id, 10));
        } else {
          selectPreviewSettings(1);
        }
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching baner types' });
      }
    },
    onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching banner types' }),
  });

  const { mutate: getContentType, isValidating: gettingContentType } = useSWR(
    [endpoints.getContentType, browserLang],
    {
      fetcher: (url, lang) => dataFetcher(url, { lang }),
      onSuccess: ({ success, data }) => {
        if (success) {
          setContentTypes([...data, { id: '#$%', content_type: 'Subcategories', order_number: '4' }]);
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
        }
      },
      onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' }),
    },
  );

  const { mutate: saveSettings } = useSWR(
    [endpoints.settings, settingsType, settingsType === 1 ? transitionSpeed : selectedTransitionType],
    {
      fetcher: (url, id, value) => dataFetcher(url, { id, value }),
      onSuccess: ({ success }) => {
        setSettingsType(undefined);
        if (success) {
          setAppSnackbar({ isVisible: true, message: 'Album settings updated' });
          setSavedSettings({ type: selectedTransitionType, speed: transitionSpeed });
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating settings' });
        }
        setUpdatingSettings(false);
      },
      onError: () => {
        setUpdatingSettings(true);
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating settings' });
      },
    },
  );

  // Deterministic settings update (was a useSWR mutation hook that auto-fired on mount
  // and relied on array-key arg-spreading; both were fragile and could surface the
  // "went wrong while updating settings" toast spuriously). Direct call + safe guards.
  const updateAllAlbumSettings = async () => {
    setUpdatingSettings(true);
    try {
      const res = await dataFetcher(endpoints.updateAllAlbumSettings, {
        value1: transitionSpeed,
        value2: selectedTransitionType,
        settingId1: 1,
        settingId2: 2,
      });
      setSettingsType(undefined);
      if (res?.success) {
        setAppSnackbar({ isVisible: true, message: res?.message || 'Album settings updated' });
        setSavedSettings({ type: selectedTransitionType, speed: transitionSpeed });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: res?.message || 'Oops! Something went wrong while updating settings' });
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating settings' });
    } finally {
      setUpdatingSettings(false);
    }
  };

  const { mutate: updateBannerType } = useSWR(
    [endpoints.updateUserLayoutType, selectedBannerType, selectedContent?.map((content) => content.id)],
    {
      fetcher: (url, layout_id, ids) => dataFetcher(url, { layout_id, ids }),
      onSuccess: ({ success }) => {
        if (success) setAppSnackbar({ isVisible: true, message: 'Banner settings updated' });
        else setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating settings' });
      },
      onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating settings' }),
    },
  );

  const { mutate: updateCategory } = useSWR(
    [endpoints.updateUserPreferences, updateType, updateType === 'category_style_id' ? selectedCategoryBox : selectedPreviewSettings],
    {
      fetcher: (url, type, value) => dataFetcher(url, { type, value }),
      onSuccess: ({ success }) => {
        if (success) setAppSnackbar({ isVisible: true, message: 'Settings updated' });
        else setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating settings' });
      },
      onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating settings' }),
    },
  );

  const { mutate: getAlbumSettings } = useSWR([endpoints.getAlbumSettings], {
    fetcher: (url) => dataFetcher(url),
    onSuccess: ({ success, transition_speed, transition_type }) => {
      if (success) {
        setTransitionSpeed(transition_speed);
        selectTransitionType(transition_type);
        setSavedSettings({ type: transition_type, speed: transition_speed });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
      }
    },
    onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' }),
  });

  useEffect(() => {
    getCategories();
    getPlaylists();
    getAlbums();
    getContentType();
    getAlbumSettings();
    getLayoutTypes();
    getUserLayouts();
  }, []);

  // The "New Category" / "New Playlist" create modals are mounted globally
  // (app.jsx) and only bump these AppContext counters on success — this page
  // has to watch them itself to refetch, otherwise the Categories/Playlists
  // counts on this page go stale after a create until a manual reload.
  useEffect(() => {
    if (categoryRefreshTrigger > 0) getCategories();
  }, [categoryRefreshTrigger]);
  useEffect(() => {
    if (playlistRefreshTrigger > 0) {
      pendingNewPlaylistRef.current = newlyCreatedPlaylistId
        ? { id: newlyCreatedPlaylistId, title: newlyCreatedPlaylistTitle }
        : null;
      getPlaylists();
    }
  }, [playlistRefreshTrigger]);

  // Legacy number-input plumbing: the #transitionSpeedInput field only exists
  // in the old SlideShowSettings markup, which this page no longer renders (the
  // speed is an ElasticSlider now). Kept inert in case that input comes back.
  useEffect(() => {
    const node = document?.getElementById('transitionSpeedInput');
    if (transitionSpeed && node) {
      const handler = (event) => {
        if (event.key === 'Enter') {
          if (parseInt(event.target.value, 10) < 4 || parseInt(event.target.value, 10) > 120) {
            setAppSnackbar({ isVisible: true, type: 'error', message: 'Invalid transition time' });
          } else {
            setSettingsType(1);
          }
        }
      };
      node.addEventListener('keyup', handler);
      return () => node.removeEventListener('keyup', handler);
    }
    return undefined;
  }, [settingsType]);

  const transitionChange = (event) => {
    setSettingsType(2);
    selectTransitionType(event.target.value);
  };

  const bannerChange = (event) => selectBannerType(event.target.value);

  const categoryChange = (event) => {
    selectCategoryBox(event.target.value);
    setUpdateType('category_style_id');
  };

  const previewChange = (event) => {
    setUpdateType('banner_preview_style_id');
    selectPreviewSettings(event.target.value);
  };

  useEffect(() => {
    if (savedContent?.length) {
      const result = savedContent.map((element) => ({
        id: element.id,
        name: selectedBannerType === '3' ? element.id : element.title,
      }));
      selectContent(result);
    }
  }, [savedContent]);

  useEffect(() => {
    if (savedBannerType) selectBannerType(savedBannerType);
  }, [savedBannerType]);

  useEffect(() => {
    if (updateType) updateCategory();
  }, [updateType, selectedCategoryBox, selectedPreviewSettings]);

  useEffect(() => {
    if (selectedBannerType !== savedBannerType) {
      selectContent([]);
    } else {
      const result = savedContent.map((element) => ({
        id: element.id,
        name: selectedBannerType === '3' ? element.id : element.title,
      }));
      selectContent(result);
    }
  }, [selectedBannerType, savedBannerType]);

  useEffect(() => {
    if (selectedBannerType === '1' || selectedBannerType === '2') updateBannerType();
  }, [selectedBannerType]);

  const [albumSettingsTarget, setAlbumSettingsTarget] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);
  // Projector Setting tab: per-user viewer mode + profile background colour.
  const { isFullscreen, setMode: setViewerMode } = useViewerMode();
  const { color: profileBgColor, setColor: setProfileBgColor } = useProfileBg();
  const [prefTab, setPrefTab] = useState(0);
  // Thumbnail tile style for the /profile rows — localStorage only (see
  // Config/featureFlags). Applies on the next /profile load.
  const [tileStyle, setTileStyle] = useState(getThumbnailTileStyle());
  const TILE_STYLE_OPTS = [
    { k: 'letterbox', label: 'Letterbox' },
    { k: 'adaptive', label: 'Adaptive' },
    { k: 'poster', label: 'Poster' },
    { k: 'cinematic', label: 'Cinematic' },
  ];
  // Video player skin — localStorage only, same as the tile style above. The
  // setter fires a window event, so a player already open restyles live.
  const [playerStyle, setPlayerStyle] = useState(getPlayerUiStyle());

  // Persist viewer prefs to the backend. Both values are sent each time; pass
  // the one being changed and the other falls back to current state. The local
  // hooks already updated the UI, so this is fire-and-forget.
  const syncViewerSettings = ({ mode, color, tile } = {}) => {
    const effMode = mode ?? (isFullscreen ? 'fullscreen' : 'popover');
    const view_type = effMode === 'fullscreen' ? 'fullscreen' : 'popup';
    const projector_background = color !== undefined ? color : profileBgColor;
    const thumbnail_style = tile !== undefined ? tile : tileStyle;
    // Keep the AppContext copy of my own settings fresh so any consumer reading
    // it (and the app-load seeder) stays consistent without a refetch.
    setMyProjectorSettings?.((prev) => ({ ...(prev || {}), projector_background, view_type, thumbnail_style }));
    dataFetcher(endpoints.updateMySettings, { projector_background, view_type, thumbnail_style })
      .catch(() => { /* non-blocking — local state already reflects the change */ });
  };

  // Active projector backdrop for preview mode. The empty value ('' = Default)
  // renders the deep mixed gradient + dimmed aurora (exactly like the real
  // projector / viewer pages via ViewerAurora); a preset paints a flat colour.
  // `bgIsDark` drives the light/dark treatment of the controls/cards on it.
  const activeBg = bgSolid(profileBgColor) || DEFAULT_BG_COLOR;
  const bgIsDark = (() => {
    const h = String(activeBg).replace('#', '');
    if (h.length < 6) return true;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 150;
  })();

  // Sections for the projector preview: parent type → rows (the categories /
  // playlists / albums), each expandable to a video carousel. reorderType drives
  // reorderItem when a row is dragged; contentTypeId drives reOrderMyContentType
  // when the sidebar (type order) is dragged; layoutType is the detail route.
  const typeId = (names) => (contentTypes || []).find(
    (ct) => names.includes((ct.content_type || '').toLowerCase()),
  )?.id;
  // Same order_number carried by contentTypes (getContentType, persisted via
  // reOrderMyContentType) that the classic Content Layout page and /profile
  // already read to decide display order — without this, the accordions here
  // always reset to the hardcoded categories/playlists/albums order on reload.
  const typeOrderNumber = (names) => {
    const ct = (contentTypes || []).find((c) => names.includes((c.content_type || '').toLowerCase()));
    return ct ? Number(ct.order_number) : null;
  };
  const ACCENT = '#15171c'; // single (black) theme accent for the preview
  const previewSections = [
    {
      key: 'categories', title: 'Categories', singular: 'category', accent: ACCENT, Icon: AnimatedCategory, reorderType: 'category', layoutType: '1', contentTypeId: typeId(['categories']), orderNumber: typeOrderNumber(['categories']), items: categoriesData || [], count: categoriesTotal, hasMore: categoriesData.length < categoriesTotal, onLoadMore: loadMoreCategories,
    },
    {
      key: 'playlists', title: 'Playlists', singular: 'playlist', accent: ACCENT, Icon: AnimatedPlaylist, reorderType: 'playlist', layoutType: '2', contentTypeId: typeId(['playlists']), orderNumber: typeOrderNumber(['playlists']), items: playlistsData || [],
    },
    {
      key: 'albums', title: 'Photo Albums', singular: 'album', accent: ACCENT, Icon: AnimatedAlbum, reorderType: 'album', layoutType: '3', contentTypeId: typeId(['photos albums', 'photo albums', 'albums']), orderNumber: typeOrderNumber(['photos albums', 'photo albums', 'albums']), items: albumsData || [],
    },
  ].sort((a, b) => (a.orderNumber ?? 999) - (b.orderNumber ?? 999));

  // Slideshow speed + transition, as cards. Rendered both in Preferences →
  // Playback and in the per-album gear dialog (which used to render the legacy
  // SlideShowSettings markup) — both drive the same state and the same
  // updateAllAlbumSettings call, so they stay one panel rather than two UIs.
  const playbackPanel = (
    <Stack spacing={2}>
      <Box sx={{
        p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden',
      }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(21,23,28,0.07)', color: '#15171c', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          >
            <AccessTimeRoundedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Slideshow speed</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Seconds per photo</Typography>
          </Box>
        </Stack>
        <ElasticSlider
          defaultValue={Number(transitionSpeed) || 5}
          startingValue={1}
          maxValue={30}
          isStepped
          stepSize={1}
          leftIcon={<span style={{ fontSize: 11, fontWeight: 700 }}>1s</span>}
          rightIcon={<span style={{ fontSize: 11, fontWeight: 700 }}>30s</span>}
          onChange={(v) => setTransitionSpeed(v)}
        />
      </Box>

      <Box sx={{
        p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
      }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box sx={{
              width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(21,23,28,0.07)', color: '#15171c', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            >
              <SwapHorizRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Transition style</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>How photos change</Typography>
            </Box>
          </Stack>
          <Select
            size="small"
            value={selectedTransitionType}
            onChange={transitionChange}
            sx={{ minWidth: 150, '& .MuiSelect-select': { fontWeight: 600, fontSize: 13 } }}
            MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontWeight: 600, fontSize: 13 } } } }}
          >
            {transitionType.map((t) => (
              <MenuItem key={t.class} value={t.class}>{t.name}</MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <TransitionPreview typeName={transitionType.find((t) => t.class === selectedTransitionType)?.name || ''} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Live preview of the selected transition
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
        <Button
          variant="contained"
          disableElevation
          onClick={() => updateAllAlbumSettings()}
          disabled={savedSettings.type === selectedTransitionType && savedSettings.speed === transitionSpeed}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3, bgcolor: '#15171c', '&:hover': { bgcolor: '#000' },
          }}
        >
          Update
        </Button>
      </Box>
    </Stack>
  );

  return (
    <Page noBottomBar>
      <Box
        className={condensed ? `${styles.v2Wrapper} ${styles.v2WrapperCondensed}` : styles.v2Wrapper}
        sx={{ position: 'relative' }}
      >
        {/* Preview mode: the exact projector backdrop from the viewer pages —
            a flat colour for a chosen preset, the deep mixed gradient for
            Default ('') — fills the whole projector page behind the content. */}
        {condensed && <ViewerAurora color={profileBgColor} />}
        {/* Preview mode: vertical projector-background colour selector on the left.
            Picking a colour applies it to the projector surface live and saves it
            as the real projector background. */}
        <Fade in={condensed} unmountOnExit timeout={320}>
          <Stack
            spacing={1.25}
            sx={{
              position: 'absolute',
              left: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 30,
              alignItems: 'center',
              p: 1,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.82)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 8px 26px rgba(16,24,40,0.16)',
            }}
          >
            {[PROFILE_BG_DEFAULT, ...PROFILE_BG_PRESETS].map((p) => {
              const active = (profileBgColor || '') === p.value;
              return (
                <Box
                  key={p.value || 'default'}
                  component="button"
                  type="button"
                  title={p.name}
                  aria-label={p.name}
                  onClick={() => { setProfileBgColor(p.value); syncViewerSettings({ color: p.value }); }}
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    p: 0,
                    // Default ('') = the deep mixed gradient, Soft Aurora = its
                    // blue/violet glow — both shown in miniature by bgSwatch.
                    background: bgSwatch(p.value),
                    border: active ? '2px solid #15171c' : '2px solid rgba(255,255,255,0.9)',
                    boxShadow: active ? '0 0 0 2px #fff inset' : '0 1px 3px rgba(0,0,0,0.18)',
                    transition: 'transform 0.16s ease',
                    '&:hover': { transform: 'scale(1.12)' },
                  }}
                />
              );
            })}
          </Stack>
        </Fade>

        {/* Preview mode: floating bottom-centre toolbar — exit preview + settings. */}
        <Fade in={condensed} unmountOnExit timeout={320}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              position: 'fixed',
              bottom: 26,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1400,
              alignItems: 'center',
              px: 1.25,
              py: 1,
              borderRadius: 999,
              bgcolor: 'rgba(22,24,32,0.82)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Button
              onClick={() => setCondense(false)}
              startIcon={<CloseRoundedIcon />}
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                px: 2,
                height: 46,
                bgcolor: '#e5484d',
                color: '#fff',
                '&:hover': { bgcolor: '#c9373b' },
              }}
            >
              Exit preview
            </Button>
            <Button
              onClick={() => { setPrefTab(0); setShowPreferences(true); }}
              startIcon={<SettingsIcon size={19} strokeWidth={2.2} />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                px: 2,
                height: 46,
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.28)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.4)' },
              }}
            >
              Settings
            </Button>
          </Stack>
        </Fade>

        <Box className={styles.v2Inner} sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{
              mb: condensed ? 1.25 : 3,
              alignItems: { sm: 'flex-end' },
              justifyContent: 'space-between',
              transition: 'margin 0.34s cubic-bezier(0.22, 0.61, 0.36, 1)',
            }}
          >
            {/* The "Content layout" header is dropped in preview mode. */}
            {condensed ? <Box /> : (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 24, sm: 28, md: 32 },
                    letterSpacing: '-0.02em',
                    color: 'text.primary',
                  }}
                >
                  {layoutText?.title || 'Content layout'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Organize categories, playlists, banner styles, and slideshow preferences.
                </Typography>
              </Box>
            )}
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
              {/* Preview mode only: the projector name joins this row. Exit +
                  Preferences live in the floating bottom toolbar. */}
              <Fade in={condensed} unmountOnExit timeout={340}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <ProjectorNameEdit variant="inline" dark={bgIsDark} {...nameEditorProps} />
                </Box>
              </Fade>

              {/* Enter preview — only shown in the normal layout view. */}
              {!condensed && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<Eye size={17} strokeWidth={2.2} />}
                  onClick={() => setCondense(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2.5,
                    px: 2,
                    bgcolor: '#15171c',
                    color: '#fff',
                    '&:hover': { bgcolor: '#000' },
                  }}
                >
                  Preview
                </Button>
              )}

              {/* Create + Preferences — dropped from the header in preview mode
                  (Preferences moves to the floating bottom toolbar). */}
              {!condensed && <CreateMenu />}

              {!condensed && (
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon size={17} strokeWidth={2.2} />}
                  onClick={() => { setPrefTab(0); setShowPreferences(true); }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2.5,
                    px: 2,
                    borderColor: '#e4e7ec',
                    color: '#15171c',
                    '&:hover': { borderColor: '#15171c', bgcolor: 'transparent' },
                    '& .lucide': { transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' },
                    '&:hover .lucide': { transform: 'rotate(80deg)' },
                  }}
                >
                  Preferences
                </Button>
              )}
            </Stack>
          </Stack>

          {/* The "Now showing on …'s Projector" row is hidden in the normal view
              — the name only appears inline in preview mode (next to the red
              close button in the header row above). */}

          {/* Render immediately — the preview has hardcoded section titles and
              its own shimmer (the `loading` prop), so it never needs to block on
              getContentType (which only supplies reorder-persist IDs). */}
          <ProjectorPreview
            sections={previewSections}
            projectorName={projectorLabel}
            previewBg={condensed ? (bgSolid(profileBgColor) || DEFAULT_BG_COLOR) : undefined}
            onOpenAlbumSettings={setAlbumSettingsTarget}
            loading={gettingCategories || gettingPlaylists || gettingAlbums}
            // Refetch just the list a row was deleted from, so counts stay in sync.
            onRefresh={(section) => {
              if (section?.key === 'categories') getCategories();
              else if (section?.key === 'playlists') getPlaylists();
              else if (section?.key === 'albums') getAlbums();
            }}
          />

          {/* Per-album slideshow settings, opened from an album row's gear icon.
              (Currently drives the global album settings; per-album persistence
              is the next stage.) */}
          <Dialog
            open={Boolean(albumSettingsTarget)}
            onClose={() => setAlbumSettingsTarget(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 1.5 } }}
          >
            {/* Same header + card body as Preferences → Playback, minus the
                pill tabs (this dialog only carries the one panel). */}
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Slideshow timer &amp; animation</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{albumSettingsTarget?.title || 'Album'}</Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setAlbumSettingsTarget(null)}
                    sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1, color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
                  >
                    Close
                  </Button>
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ p: 3, maxHeight: '58vh', overflowY: 'auto' }}>
                {playbackPanel}
              </Box>
            </DialogContent>
          </Dialog>

          {/* Preferences — pill-nav tabbed modal opened from the sidebar */}
          <Dialog
            open={showPreferences}
            onClose={() => setShowPreferences(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 1.5 } }}
          >
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Preferences</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Playback, projector &amp; preview</Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setShowPreferences(false)}
                    sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1, color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
                  >
                    Close
                  </Button>
                </Stack>
                <PillNav
                  items={['Playback', 'Projector Setting', 'Preview Style']}
                  activeIndex={prefTab}
                  onChange={setPrefTab}
                  accent="#15171c"
                />
              </Box>
              <Divider />

              <Box sx={{ p: 3, maxHeight: '58vh', overflowY: 'auto' }}>
                {prefTab === 0 && playbackPanel}
                {prefTab === 1 && (
                  <Stack spacing={2}>
                    {/* Video/album preview mode — per-user popover vs fullscreen. */}
                    <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{
                          width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(21,23,28,0.07)', color: '#15171c', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                          <Eye size={18} strokeWidth={2.2} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Video &amp; album preview</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>How a video or album opens</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        {[{ k: 'popover', label: 'Popup' }, { k: 'fullscreen', label: 'Full screen' }].map((opt) => {
                          const active = (opt.k === 'fullscreen') === isFullscreen;
                          return (
                            <Button
                              key={opt.k}
                              onClick={() => { setViewerMode(opt.k); syncViewerSettings({ mode: opt.k }); }}
                              disableElevation
                              variant={active ? 'contained' : 'outlined'}
                              sx={{
                                flex: 1,
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: 2,
                                ...(active
                                  ? { bgcolor: '#15171c', '&:hover': { bgcolor: '#000' } }
                                  : { color: '#15171c', borderColor: '#d0d5dd' }),
                              }}
                            >
                              {opt.label}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>

                    {/* Projector background colour — 5 presets, applied to /profile. */}
                    <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{
                          width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(21,23,28,0.07)', color: '#15171c', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                          <Palette size={18} strokeWidth={2.2} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Projector background</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Shown on your projector page</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap' }} useFlexGap>
                        {/* Default (aurora) — selected when no colour override is set. */}
                        <Box
                          component="button"
                          type="button"
                          onClick={() => { setProfileBgColor(''); syncViewerSettings({ color: '' }); }}
                          title="Default (Soft Aurora)"
                          aria-label="Default (Soft Aurora)"
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            p: 0,
                            background: bgSwatch(''),
                            border: !profileBgColor ? '3px solid #15171c' : '2px solid #d0d5dd',
                            boxShadow: !profileBgColor ? 'inset 0 0 0 2px #fff' : 'none',
                            transition: 'transform 0.15s ease, border-color 0.15s ease',
                            '&:hover': { transform: 'translateY(-2px)' },
                          }}
                        />
                        {PROFILE_BG_PRESETS.map((p) => {
                          const active = profileBgColor === p.value;
                          return (
                            <Box
                              key={p.value}
                              component="button"
                              type="button"
                              onClick={() => { const c = active ? '' : p.value; setProfileBgColor(c); syncViewerSettings({ color: c }); }}
                              title={p.name}
                              aria-label={p.name}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                p: 0,
                                background: bgSwatch(p.value),
                                border: active ? '3px solid #15171c' : '2px solid #d0d5dd',
                                boxShadow: active ? 'inset 0 0 0 2px #fff' : 'none',
                                transition: 'transform 0.15s ease, border-color 0.15s ease',
                                '&:hover': { transform: 'translateY(-2px)' },
                              }}
                            />
                          );
                        })}
                      </Stack>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.25 }}>
                        Applies to your /profile and syncs to your account.
                      </Typography>
                    </Box>

                    {/* Thumbnail tile style — how video tiles are shaped on
                        /profile. Stored in localStorage only (no account sync). */}
                    <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{
                          width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(21,23,28,0.07)', color: '#15171c', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                          <LayoutGrid size={18} strokeWidth={2.2} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Thumbnail style</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>How video tiles are shaped on your projector</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
                        {TILE_STYLE_OPTS.map((opt) => {
                          const active = tileStyle === opt.k;
                          return (
                            <Button
                              key={opt.k}
                              onClick={() => { setThumbnailTileStyle(opt.k); setTileStyle(opt.k); syncViewerSettings({ tile: opt.k }); }}
                              disableElevation
                              variant={active ? 'contained' : 'outlined'}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: 2,
                                ...(active
                                  ? { bgcolor: '#15171c', '&:hover': { bgcolor: '#000' } }
                                  : { color: '#15171c', borderColor: '#d0d5dd' }),
                              }}
                            >
                              {opt.label}
                            </Button>
                          );
                        })}
                      </Stack>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.25 }}>
                        Applies to your /profile (on its next load) and syncs to your account.
                      </Typography>
                    </Box>

                    {/* Video player skin — which control-bar style the video
                        player wears. Stored in localStorage only (no account
                        sync); the same list is offered by the gear button
                        inside the player itself. */}
                    <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{
                          width: 38, height: 38, borderRadius: 2, bgcolor: 'rgba(21,23,28,0.07)', color: '#15171c', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                          <MonitorPlay size={18} strokeWidth={2.2} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Video player style</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>How the controls look while a video plays</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
                        {PLAYER_UI_STYLE_OPTIONS.map((opt) => {
                          const active = playerStyle === opt.key;
                          return (
                            <Button
                              key={opt.key}
                              onClick={() => { setPlayerUiStyle(opt.key); setPlayerStyle(opt.key); }}
                              disableElevation
                              variant={active ? 'contained' : 'outlined'}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: 2,
                                ...(active
                                  ? { bgcolor: '#15171c', '&:hover': { bgcolor: '#000' } }
                                  : { color: '#15171c', borderColor: '#d0d5dd' }),
                              }}
                            >
                              {opt.label}
                            </Button>
                          );
                        })}
                      </Stack>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.25 }}>
                        {`${(PLAYER_UI_STYLE_OPTIONS.find((o) => o.key === playerStyle) || {}).desc || ''} — saved on this device, and switchable from the gear inside the player.`}
                      </Typography>
                    </Box>
                  </Stack>
                )}
                {prefTab === 2 && (
                  <ComingSoon
                    title="Preview Style"
                    desc="Choose how content previews look behind the banner — motion, framing and layout options are on the way."
                    Icon={Eye}
                  />
                )}
              </Box>
            </DialogContent>
          </Dialog>
        </Box>
      </Box>
    </Page>
  );
};

export const ContentLayoutV2 = () => (
  <V2ThemeProvider>
    <ContentLayoutInner />
  </V2ThemeProvider>
);
